export type RegistryAttestation = {
  id: number
  predicateType: string
  createdAt: string
  runInvocationUri?: string
  workflowFile?: string
  repository: string
}

export type StorageRecord = {
  id: number
  source_registry: string
  registry_url: string
  registry_repository_name: string
  artifact_url: string
  artifact_path: string | null
  created_at: string
  updated_at: string
  status: string
}

export type Artifact = {
  artifact_metadata_id: number
  owner_id: number
  artifact_name: string
  version?: string
  created_at: string
  updated_at: string
  attestation_id?: number
  repository_id?: number
  repository_name?: string
  source_registry?: string
  app_name?: string
  tags?: string[]
  deployment_count?: number
  repository_is_private?: boolean
  digest?: string
}

export function artifactIsDetectedBy(artifact: Artifact): string | undefined {
  const normalizedAppName = artifact.app_name?.trim()
  if (normalizedAppName) {
    return normalizedAppName
  }
  return artifact.source_registry || undefined
}

export type Deployment = {
  deployment_name: string
  logical_environment: string
  physical_environment: string
  cluster: string
  created_at: string
  updated_at: string
  deleted_at?: string
  runtime_risks: string[]
}

export const SortOrder = {
  NewestCreated: 'created-desc',
  OldestCreated: 'created-asc',
  NewestUpdated: 'updated-desc',
  OldestUpdated: 'updated-asc',
} as const

export type SortOrderType = (typeof SortOrder)[keyof typeof SortOrder]

export type SortField = SortOrderType extends `${infer F}-${string}` ? F : never
export type SortDirection = SortOrderType extends `${string}-${infer D}` ? D : never

export const DEFAULT_SORT: SortOrderType = SortOrder.NewestCreated

export const SORT_FIELD_LABELS: Record<SortField, string> = {
  created: 'Created on',
  updated: 'Last updated',
}

export const SORT_DIRECTION_LABELS: Record<SortDirection, string> = {
  desc: 'Newest',
  asc: 'Oldest',
}

// Button labels: for 'created' we show direction (Newest/Oldest); for other fields we show the field name.
export const SORT_BUTTON_LABELS: Record<SortField, Record<SortDirection, string> | string> = {
  created: {desc: 'Newest', asc: 'Oldest'},
  updated: 'Updated',
}

const VALID_SORT_VALUES = new Set<string>(Object.values(SortOrder))

export function isValidSortOrder(value: string): value is SortOrderType {
  return VALID_SORT_VALUES.has(value)
}

export function parseSortOrder(sort: SortOrderType): {field: SortField; direction: SortDirection} {
  switch (sort) {
    case SortOrder.NewestCreated:
      return {field: 'created', direction: 'desc'}
    case SortOrder.OldestCreated:
      return {field: 'created', direction: 'asc'}
    case SortOrder.NewestUpdated:
      return {field: 'updated', direction: 'desc'}
    case SortOrder.OldestUpdated:
      return {field: 'updated', direction: 'asc'}
    default:
      return {field: 'created', direction: 'desc'}
  }
}

export function buildSortOrder(field: SortField, direction: SortDirection): SortOrderType {
  return `${field}-${direction}`
}

export type PageInfo = {
  totalCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  currentPage: number
  offset: number
  pageSize: number
  totalPages: number
}

export type DeploymentPageInfo = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export type ArtifactRegistryShowRoutePayload = {
  artifact: Artifact
  attestations: RegistryAttestation[]
  deployments: Deployment[]
  storageRecord?: StorageRecord
  deploymentPageInfo?: DeploymentPageInfo
}

export type ArtifactRegistryIndexPayload = {
  artifacts: Artifact[]
  totalCount?: number
  pageInfo?: PageInfo
}
